import { summaryForPrompt } from './compute';
import { localToday } from './plan';
import type { Chat } from './plan';

/**
 * **계산이 업무 규칙대로 셌는지 본다.** 같은 근태 질문이 한 판은 「거리 0 초과면 모바일」로 세어 25.97% 를,
 * 다른 판은 「거리 값이 있으면 모바일」로 세어 100% 를 냈다(2026-10-20). 코드 검증(`verifyResult`)은 칸과
 * 빈 값을 볼 뿐 셈법이 규칙과 맞는지는 모른다.
 *
 * 스스로 고치게 하는 것은 기댈 데가 없으면 나빠지기 쉽다(연구 보고서 「에이전트 루프 엔지니어링」). 그래서 이
 * 검토는 **스킬 규칙이라는 바깥 기준이 있을 때만** 부르고, 판정이 흐리면 통과시킨다 — 답을 막지 않는다.
 */
export async function reviewResult(input: {
  question: string;
  rules: string;
  code: string;
  value: unknown;
  chat: Chat;
}): Promise<{ ok: boolean; reason?: string }> {
  const prompt = `계산이 업무 규칙대로 셌는지 검토한다. 직원 질문에 이 계산 결과로 답해도 되는지 판정하라.

- 업무 규칙과 어긋나게 세거나 가른 곳이 있으면 거절한다(예: 규칙은 「0 보다 크면」인데 「값이 있으면」으로 셌다)
- 결과가 질문에 답하지 않으면 거절한다
- 숫자가 그럴듯한지(100%·0% 같은 극단값)도 규칙과 함께 본다. 규칙대로 셌는데 극단값이면 통과다
- 규칙에 없는 취향은 따지지 마라
- 사람이 말하지 않은 기간·기준을 계산이 정한 것은 거절 사유가 아니다 — 답이 그 가정을 밝힌다.
  「기간을 확인해야 한다」·「기준을 물어야 한다」로 거절하지 마라. 규칙이 그것을 정해 두었는데 다르게 썼을 때만 거절한다
- 규칙은 그 화면 자료를 셀 때의 셈법이다. 질문이 묻지 않은 보고 형식(월별 표·순증감 보고 등)을 요구하며 거절하지 마라.
  질문이 규칙이 다루지 않는 것을 다른 자료로 판정했으면 그 부분은 규칙 위반이 아니다
- 워드·엑셀·PDF 같은 파일과 문서 꾸밈은 다음 단계가 이 결과로 만든다. 계산이 파일을 만들지 않은 것은 거절 사유가 아니다
- 오늘은 ${localToday()}이다. 「오늘까지」는 이 날짜까지다. 계산 코드나 결과에 찍힌 다른 날짜를 오늘로 보고 거절하지 마라
  (파이썬의 date.today()는 하루 이를 수 있다)
- 답은 JSON 한 줄: {"ok": true} 또는 {"ok": false, "reason": "무엇을 어떻게 고쳐야 하는지 한 문장"}

질문: ${input.question}

업무 규칙:
${input.rules}

계산 코드:
${input.code}

계산 결과:
${summaryForPrompt(input.value)}`;
  try {
    const said = await input.chat(prompt, { step: 'review' });
    const json = /\{[\s\S]*\}/.exec(said)?.[0];
    const parsed = json ? JSON.parse(json) as { ok?: unknown; reason?: unknown } : undefined;
    if (parsed?.ok === false) {
      const reason = typeof parsed.reason === 'string' ? parsed.reason.trim() : '';
      return reason ? { ok: false, reason } : { ok: true };
    }
    return { ok: true };
  } catch {
    // 검토는 덤이다. 호출이나 해석이 실패했다고 손에 쥔 답을 막지 않는다.
    return { ok: true };
  }
}
